// Tab Preview Page — visual preview gallery for the user's tabs.
// Data flows from GET /tab-preview/batch (enriched with title/url) and
// the per-row actions hit POST /tab-preview/:id/capture and DELETE
// /tab-preview/:id. All user input is debounced, the auto-refresh
// interval is a real setInterval, and per-card refresh/delete state
// is tracked in a Set instead of one global flag.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye,
  Image as ImageIcon,
  RefreshCw,
  Grid3X3,
  List,
  ZoomIn,
  ExternalLink,
  Clock,
  Trash2,
  Search,
  AlertCircle,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient, { type TabPreview, type TabPreviewStats } from '../services/api-client';
import { useTabMonitorSessionOptional, WS_SESSION_STORAGE_KEY } from '@/context/TabMonitorSessionContext';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal, ConfirmDialog } from '../components/ui/Modal';
import { EmptyState, ErrorState, NoResultsState } from '../components/ui/EmptyState';
import { LoadingState } from '../components/ui/LoadingState';
import { Badge } from '../components/ui/Badge';
import { cn } from '@/utils';

type Layout = 'grid' | 'list';
type StaleFilter = 'all' | 'stale' | 'fresh';
type CardSize = 'small' | 'medium' | 'large';

const REFRESH_OPTIONS: { label: string; value: number }[] = [
  { label: 'Off', value: 0 },
  { label: '1 min', value: 60 },
  { label: '5 min', value: 300 },
  { label: '15 min', value: 900 },
];

const SIZE_CLASS: Record<CardSize, string> = {
  small: 'w-44 h-32',
  medium: 'w-60 h-40',
  large: 'w-80 h-56',
};

const STALE_BADGE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 250;
const AUTO_CAPTURE_CONCURRENCY = 3;
const PREVIEW_POLL_MS = 15000;
const EXT_AUTO_CAPTURE_KEY = 'khiimori_ext_auto_capture_at';
const EXT_AUTO_CAPTURE_COOLDOWN_MS = 10 * 60 * 1000;

function isRenderablePreviewImage(src?: string | null): boolean {
  const value = src?.trim() ?? '';
  if (!value) return false;
  if (value.startsWith('data:image/')) {
    const comma = value.indexOf(',');
    if (comma < 0 || value.length <= comma + 1) return false;
    const payload = value.slice(comma + 1);
    if (payload.length < 128) return false;
    try {
      const binary = atob(payload.slice(0, 64));
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
      if (bytes[0] === 0xff && bytes[1] === 0xd8) return true;
      if (bytes[0] === 0x89 && bytes[1] === 0x50) return true;
      if (bytes[0] === 0x47 && bytes[1] === 0x49) return true;
      return false;
    } catch {
      return false;
    }
  }
  const lower = value.toLowerCase();
  return /\.(png|jpe?g|webp|gif|svg)(\?|$)/.test(lower);
}

function hasPreviewImage(p: TabPreview): boolean {
  return isRenderablePreviewImage(p.screenshot) || isRenderablePreviewImage(p.thumbnail);
}

function faviconFromUrl(url: string): string {
  try {
    const host = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`;
  } catch {
    return '';
  }
}

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = [];
  let index = 0;
  async function worker() {
    while (index < items.length) {
      const current = index;
      index += 1;
      results[current] = await fn(items[current]);
    }
  }
  const workers = Array.from({ length: Math.min(limit, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

function formatBytes(n?: number): string {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatCaptured(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function deriveIsStale(p: TabPreview): boolean {
  if (p.is_stale) return true;
  const t = new Date(p.captured_at).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t > STALE_BADGE_THRESHOLD_MS;
}

function normalize(raw: Partial<TabPreview> & { id?: string; tab_id?: string }): TabPreview | null {
  const tabId = (raw.tab_id ?? raw.id ?? '').toString();
  if (!tabId) return null;
  return {
    id: tabId,
    tab_id: tabId,
    title: raw.title ?? 'Untitled tab',
    url: raw.url ?? '',
    favicon: (raw as { favicon?: string | null }).favicon ?? undefined,
    screenshot: raw.screenshot,
    thumbnail: raw.thumbnail,
    width: raw.width,
    height: raw.height,
    format: raw.format,
    size_bytes: raw.size_bytes,
    captured_at: raw.captured_at ?? new Date().toISOString(),
    is_stale: !!raw.is_stale,
    metadata: raw.metadata,
  };
}

export const TabPreviewPage: React.FC = () => {
  const sessionCtx = useTabMonitorSessionOptional();
  const workspaceSessionId =
    sessionCtx?.workspaceSessionId ?? sessionStorage.getItem(WS_SESSION_STORAGE_KEY);

  const [previews, setPreviews] = useState<TabPreview[]>([]);
  const [stats, setStats] = useState<TabPreviewStats | null>(null);
  const [selected, setSelected] = useState<TabPreview | null>(null);
  const [layout, setLayout] = useState<Layout>('grid');
  const [cardSize, setCardSize] = useState<CardSize>('medium');
  const [refreshInterval, setRefreshInterval] = useState<number>(0);
  const [staleFilter, setStaleFilter] = useState<StaleFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [rowBusy, setRowBusy] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TabPreview | null>(null);
  // Epoch ms of the most recent successful data load. Drives the
  // "Updated at" timestamp; the value is also bumped once a minute by
  // a single setInterval so the displayed clock doesn't drift.
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const serverCaptureAttemptedRef = useRef<Set<string>>(new Set());
  const captureInFlightRef = useRef(false);

  // Debounce search input so the filter doesn't thrash on every keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [search]);

  const loadPreviews = useCallback(async (showSpinner: boolean, autoCapture = false): Promise<TabPreview[]> => {
    if (showSpinner) setRefreshingAll(true);
    let normalized: TabPreview[] = [];
    try {
      const [list, s] = await Promise.all([
        apiClient.getTabPreviews(workspaceSessionId ?? undefined),
        apiClient.getTabPreviewStats().catch((err) => {
          if (showSpinner) {
            toast.warning(
              err instanceof Error
                ? `Couldn't load preview stats: ${err.message}`
                : "Couldn't load preview stats — totals may be out of date.",
            );
          }
          return null;
        }),
      ]);
      normalized = list
        .map((p) => normalize(p))
        .filter((p): p is TabPreview => p !== null);

      const missing = normalized.filter((p) => {
        if (!p.url || hasPreviewImage(p)) return false;
        if (!autoCapture) return true;
        return !serverCaptureAttemptedRef.current.has(p.tab_id);
      });
      if (autoCapture && missing.length > 0) {
        for (const p of missing) {
          serverCaptureAttemptedRef.current.add(p.tab_id);
        }
        const captured = await mapWithConcurrency(missing, AUTO_CAPTURE_CONCURRENCY, async (preview) => {
          try {
            const updated = await apiClient.captureTabPreview(preview.tab_id, {
              url: preview.url,
              width: preview.width,
              height: preview.height,
              format: preview.format,
            });
            return normalize(updated);
          } catch {
            return null;
          }
        });
        const capturedById = new Map(
          captured
            .filter((p): p is TabPreview => p !== null && hasPreviewImage(p))
            .map((p) => [p.tab_id, p]),
        );
        normalized = normalized.map((p) => capturedById.get(p.tab_id) ?? p);
      }

      setPreviews(normalized);
      setStats(
        s ?? {
          total_previews: normalized.length,
          total_size_bytes: normalized.reduce((sum, p) => sum + (p.size_bytes ?? 0), 0),
          stale_count: normalized.filter(deriveIsStale).length,
          by_format: normalized.reduce<Record<string, number>>((acc, p) => {
            const fmt = p.format ?? 'unknown';
            acc[fmt] = (acc[fmt] ?? 0) + 1;
            return acc;
          }, {}),
        },
      );
      setError(null);
      setLastUpdated(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load previews');
    } finally {
      setLoading(false);
      setRefreshingAll(false);
    }
    return normalized;
  }, [workspaceSessionId]);


  // Auto-refresh interval
  const refreshIntervalRef = useRef(refreshInterval);
  refreshIntervalRef.current = refreshInterval;
  useEffect(() => {
    if (refreshInterval <= 0) return;
    const id = window.setInterval(() => {
      void loadPreviews(false);
    }, refreshInterval * 1000);
    return () => window.clearInterval(id);
  }, [refreshInterval, loadPreviews]);

  // Re-render the "Updated at" clock once a minute so the displayed
  // time doesn't drift. State is bumped to a new Date().getTime() so
  // React re-renders even when the minute boundary doesn't land on
  // an exact integer minute.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => {
      forceTick((n) => (n + 1) % 1_000_000);
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Per-row busy flag
  const setRowState = useCallback((tabId: string, busy: boolean) => {
    setRowBusy((prev) => {
      const next = new Set(prev);
      if (busy) next.add(tabId);
      else next.delete(tabId);
      return next;
    });
  }, []);

  const handleRefreshOne = useCallback(
    async (preview: TabPreview) => {
      if (!preview.url) {
        toast.error('Cannot recapture: this tab has no URL.');
        return;
      }
      setRowState(preview.tab_id, true);
      try {
        const updated = await apiClient.captureTabPreview(preview.tab_id, {
          url: preview.url,
          width: preview.width,
          height: preview.height,
          format: preview.format,
        });
        const norm = normalize(updated);
        if (norm) {
          setPreviews((prev) => prev.map((p) => (p.tab_id === norm.tab_id ? norm : p)));
          if (selected?.tab_id === norm.tab_id) setSelected(norm);
          setLastUpdated(Date.now());
          toast.success(`Recaptured ${norm.title || 'preview'}`);
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to recapture preview');
      } finally {
        setRowState(preview.tab_id, false);
      }
    },
    [selected, setRowState],
  );

  const handleDelete = useCallback(
    async (preview: TabPreview) => {
      setRowState(preview.tab_id, true);
      try {
        await apiClient.deleteTabPreview(preview.tab_id);
        setPreviews((prev) => prev.filter((p) => p.tab_id !== preview.tab_id));
        if (selected?.tab_id === preview.tab_id) setSelected(null);
        toast.success(`Removed preview for ${preview.title || 'tab'}`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete preview');
      } finally {
        setRowState(preview.tab_id, false);
        setPendingDelete(null);
      }
    },
    [selected, setRowState],
  );

  const visiblePreviews = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return previews.filter((p) => {
      if (staleFilter === 'stale' && !deriveIsStale(p)) return false;
      if (staleFilter === 'fresh' && deriveIsStale(p)) return false;
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        p.url.toLowerCase().includes(q)
      );
    });
  }, [previews, debouncedSearch, staleFilter]);

  const staleCount = useMemo(() => previews.filter(deriveIsStale).length, [previews]);

  const triggerExtensionCaptureAndReload = useCallback(
    (showSpinner: boolean) => {
      if (captureInFlightRef.current) return;
      captureInFlightRef.current = true;
      if (showSpinner) {
        serverCaptureAttemptedRef.current = new Set();
        setRefreshingAll(true);
      }
      window.postMessage({ type: 'KHIIMORI_CAPTURE_TAB_SCREENSHOTS' }, '*');

      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        captureInFlightRef.current = false;
        window.removeEventListener('message', onMessage);
        void loadPreviews(showSpinner, true);
      };

      const onMessage = (event: MessageEvent) => {
        if (event.source !== window) return;
        if (event.data?.type === 'KHIIMORI_SCREENSHOTS_CAPTURED') {
          finish();
        }
      };

      window.addEventListener('message', onMessage);
      window.setTimeout(finish, 45000);
    },
    [loadPreviews],
  );

  // Reset per-tab capture tracking when workspace changes.
  useEffect(() => {
    serverCaptureAttemptedRef.current = new Set();
  }, [workspaceSessionId]);

  // Load previews, then server-capture any tabs still missing thumbnails.
  // Auth-walled tabs (Gmail, Calendar, etc.) need the extension — trigger it when gaps remain.
  useEffect(() => {
    setLoading(true);
    void (async () => {
      let list = await loadPreviews(false, false);
      list = await loadPreviews(false, true);
      const needsExtension = list.some((p) => p.url && !hasPreviewImage(p));
      if (needsExtension) {
        const lastAuto = Number(sessionStorage.getItem(EXT_AUTO_CAPTURE_KEY) || 0);
        if (Date.now() - lastAuto > EXT_AUTO_CAPTURE_COOLDOWN_MS) {
          sessionStorage.setItem(EXT_AUTO_CAPTURE_KEY, String(Date.now()));
          triggerExtensionCaptureAndReload(false);
        }
      }
    })();
  }, [workspaceSessionId, loadPreviews, triggerExtensionCaptureAndReload]);

  // Poll for newly opened tabs and capture previews for tabs we have not tried yet.
  useEffect(() => {
    const id = window.setInterval(() => {
      void loadPreviews(false, true);
    }, PREVIEW_POLL_MS);
    return () => window.clearInterval(id);
  }, [loadPreviews]);

  // Refresh when the extension uploads a new screenshot.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window) return;
      const type = event.data?.type;
      if (type === 'KHIIMORI_SCREENSHOTS_CAPTURED' || type === 'KHIIMORI_TAB_PREVIEW_UPDATED') {
        void loadPreviews(false, true);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [loadPreviews]);

  const headerActions = (
    <Button
      variant="gradient"
      leftIcon={<RefreshCw className={cn('h-4 w-4', refreshingAll && 'animate-spin')} />}
      onClick={() => triggerExtensionCaptureAndReload(true)}
      isLoading={refreshingAll && previews.length > 0}
    >
      Refresh all
    </Button>
  );

  return (
    <PageLayout
      title="Tab Previews"
      description="Visual thumbnails of your open browser tabs. Re-capture stale shots, delete junk, and group by recency."
      icon={Eye}
      actions={headerActions}
    >
      <div className="space-y-6">
        {/* Stats row — always shown; uses placeholders when the stats
            call failed so users aren't misled into thinking the data
            is missing. */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard
            label="Total"
            value={stats ? stats.total_previews : '—'}
          />
          <StatCard
            label="Stale"
            value={stats ? stats.stale_count : '—'}
            tone="warning"
          />
          <StatCard
            label="On disk"
            value={stats ? formatBytes(stats.total_size_bytes) : '—'}
          />
          <StatCard
            label="Formats"
            value={stats ? Object.keys(stats.by_format ?? {}).length : '—'}
          />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col gap-4 rounded-2xl border border-khiimori-border bg-khiimori-bg-elevated/30 p-4 backdrop-blur-xl">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-1 items-center gap-2">
              <div className="relative w-full max-w-sm">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by title or URL"
                  leftIcon={<Search className="h-4 w-4" />}
                  aria-label="Search previews"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-khiimori-text-muted hover:text-khiimori-text-primary"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex rounded-lg border border-khiimori-border p-0.5">
                {(['all', 'fresh', 'stale'] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setStaleFilter(opt)}
                    className={cn(
                      'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                      staleFilter === opt
                        ? 'bg-primary-500/20 text-primary-300'
                        : 'text-khiimori-text-muted hover:text-khiimori-text-primary',
                    )}
                  >
                    {opt === 'all' ? 'All' : opt === 'stale' ? 'Stale' : 'Fresh'}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-sm text-khiimori-text-muted">
                <Clock className="h-4 w-4" />
                <span>Auto-refresh</span>
                <select
                  value={refreshInterval}
                  onChange={(e) => setRefreshInterval(Number(e.target.value))}
                  className="h-9 rounded-md border border-khiimori-border bg-khiimori-bg-elevated px-2 text-sm text-khiimori-text-primary focus:border-primary-500 focus:outline-none"
                  aria-label="Auto-refresh interval"
                >
                  {REFRESH_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex rounded-lg border border-khiimori-border p-0.5">
                {(['small', 'medium', 'large'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setCardSize(s)}
                    className={cn(
                      'px-2 py-1 text-xs font-medium rounded-md transition-colors',
                      cardSize === s
                        ? 'bg-primary-500/20 text-primary-300'
                        : 'text-khiimori-text-muted hover:text-khiimori-text-primary',
                    )}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
              <div className="flex rounded-lg border border-khiimori-border p-0.5">
                <button
                  type="button"
                  onClick={() => setLayout('grid')}
                  aria-label="Grid layout"
                  aria-pressed={layout === 'grid'}
                  className={cn(
                    'p-1.5 rounded-md transition-colors',
                    layout === 'grid'
                      ? 'bg-primary-500/20 text-primary-300'
                      : 'text-khiimori-text-muted hover:text-khiimori-text-primary',
                  )}
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setLayout('list')}
                  aria-label="List layout"
                  aria-pressed={layout === 'list'}
                  className={cn(
                    'p-1.5 rounded-md transition-colors',
                    layout === 'list'
                      ? 'bg-primary-500/20 text-primary-300'
                      : 'text-khiimori-text-muted hover:text-khiimori-text-primary',
                  )}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-khiimori-text-muted">
            <span>
              {visiblePreviews.length} of {previews.length} preview
              {previews.length === 1 ? '' : 's'}
              {staleCount > 0 ? ` · ${staleCount} stale` : ''}
            </span>
            <span>
              {lastUpdated
                ? `Updated ${new Date(lastUpdated).toLocaleTimeString()}`
                : 'Updating…'}
            </span>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <LoadingState message="Loading previews..." variant="spinner" />
        ) : error && previews.length === 0 ? (
          <ErrorState onRetry={() => void loadPreviews(true, true)} error={error} />
        ) : previews.length === 0 ? (
          <EmptyState
            title="No previews yet"
            description="Open a few tabs in the browser extension to see their thumbnails here."
            action={{
              label: 'Refresh',
              onClick: () => void loadPreviews(true, true),
              icon: <RefreshCw className="h-4 w-4" />,
            }}
          />
        ) : visiblePreviews.length === 0 ? (
          <NoResultsState
            query={debouncedSearch}
            onClearFilters={() => {
              setSearch('');
              setStaleFilter('all');
            }}
          />
        ) : layout === 'grid' ? (
          <PreviewGrid
            previews={visiblePreviews}
            size={SIZE_CLASS[cardSize]}
            busyIds={rowBusy}
            onZoom={setSelected}
            onRefresh={handleRefreshOne}
            onDelete={(p) => setPendingDelete(p)}
          />
        ) : (
          <PreviewList
            previews={visiblePreviews}
            size={SIZE_CLASS[cardSize]}
            busyIds={rowBusy}
            onZoom={setSelected}
            onRefresh={handleRefreshOne}
            onDelete={(p) => setPendingDelete(p)}
          />
        )}

        {error && previews.length > 0 && (
          <div
            role="alert"
            className="flex items-center gap-2 rounded-lg border border-warning-500/30 bg-warning-500/10 px-4 py-3 text-sm text-warning-300"
          >
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void loadPreviews(true, true)}
              className="ml-auto"
            >
              Retry
            </Button>
          </div>
        )}
      </div>

      {/* Detail modal */}
      <Modal
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.title || 'Preview'}
        description={selected?.url}
        size="xl"
      >
        {selected && (
          <PreviewDetail
            preview={selected}
            busy={rowBusy.has(selected.tab_id)}
            onRefresh={() => void handleRefreshOne(selected)}
            onDelete={() => setPendingDelete(selected)}
          />
        )}
      </Modal>

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && void handleDelete(pendingDelete)}
        title="Delete preview?"
        message={
          pendingDelete
            ? `This will remove the preview thumbnail for "${pendingDelete.title}". You can recapture it later.`
            : ''
        }
        confirmText="Delete"
        variant="danger"
        isLoading={pendingDelete ? rowBusy.has(pendingDelete.tab_id) : false}
      />
    </PageLayout>
  );
};

function StatCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  tone?: 'default' | 'warning';
}) {
  return (
    <div className="rounded-xl border border-khiimori-border bg-khiimori-bg-elevated/30 p-3">
      <div className="text-xs uppercase tracking-wide text-khiimori-text-muted">{label}</div>
      <div
        className={cn(
          'mt-1 text-2xl font-semibold',
          tone === 'warning' ? 'text-warning-400' : 'text-khiimori-text-primary',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function PreviewGrid({
  previews,
  size,
  busyIds,
  onZoom,
  onRefresh,
  onDelete,
}: {
  previews: TabPreview[];
  size: string;
  busyIds: Set<string>;
  onZoom: (p: TabPreview) => void;
  onRefresh: (p: TabPreview) => void;
  onDelete: (p: TabPreview) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      <AnimatePresence>
        {previews.map((preview, idx) => (
          <PreviewCard
            key={preview.tab_id}
            preview={preview}
            index={idx}
            size={size}
            busy={busyIds.has(preview.tab_id)}
            onZoom={onZoom}
            onRefresh={onRefresh}
            onDelete={onDelete}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

function PreviewList({
  previews,
  size,
  busyIds,
  onZoom,
  onRefresh,
  onDelete,
}: {
  previews: TabPreview[];
  size: string;
  busyIds: Set<string>;
  onZoom: (p: TabPreview) => void;
  onRefresh: (p: TabPreview) => void;
  onDelete: (p: TabPreview) => void;
}) {
  return (
    <div className="space-y-3">
      <AnimatePresence>
        {previews.map((preview, idx) => (
          <motion.div
            key={preview.tab_id}
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ delay: idx * 0.02 }}
            className="flex items-center gap-4 rounded-xl border border-khiimori-border bg-khiimori-bg-elevated/30 p-3 hover:bg-khiimori-bg-elevated/50 transition-colors"
          >
            <PreviewThumbnail
              preview={preview}
              size={size}
              busy={busyIds.has(preview.tab_id)}
              onZoom={() => onZoom(preview)}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="truncate font-semibold text-khiimori-text-primary">
                  {preview.title}
                </h3>
                {deriveIsStale(preview) && (
                  <Badge variant="warning" size="sm">
                    Stale
                  </Badge>
                )}
              </div>
              <p className="truncate text-sm text-khiimori-text-muted">{preview.url}</p>
              <span className="text-xs text-khiimori-text-muted">
                Captured {formatCaptured(preview.captured_at)}
                {preview.format ? ` · ${preview.format.toUpperCase()}` : ''}
              </span>
            </div>
            <RowActions
              preview={preview}
              busy={busyIds.has(preview.tab_id)}
              onZoom={() => onZoom(preview)}
              onRefresh={() => onRefresh(preview)}
              onDelete={() => onDelete(preview)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

function PreviewCard({
  preview,
  index,
  size,
  busy,
  onZoom,
  onRefresh,
  onDelete,
}: {
  preview: TabPreview;
  index: number;
  size: string;
  busy: boolean;
  onZoom: (p: TabPreview) => void;
  onRefresh: (p: TabPreview) => void;
  onDelete: (p: TabPreview) => void;
}) {
  const isStale = deriveIsStale(preview);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ delay: index * 0.02 }}
      className="group overflow-hidden rounded-xl border border-khiimori-border bg-khiimori-bg-elevated/30 hover:bg-khiimori-bg-elevated/50 transition-colors"
    >
      <PreviewThumbnail
        preview={preview}
        size={size}
        busy={busy}
        onZoom={() => onZoom(preview)}
        showOverlayActions
        onRefresh={() => onRefresh(preview)}
        onDelete={() => onDelete(preview)}
      />
      <div className="p-3">
        <div className="flex items-center gap-2">
          <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-khiimori-text-primary">
            {preview.title}
          </h3>
          {isStale && (
            <Badge variant="warning" size="sm">
              Stale
            </Badge>
          )}
        </div>
        <p className="truncate text-xs text-khiimori-text-muted">{preview.url}</p>
        <div className="mt-2 flex items-center justify-between text-xs text-khiimori-text-muted">
          <span>{formatCaptured(preview.captured_at)}</span>
          <RowActions
            preview={preview}
            busy={busy}
            onZoom={() => onZoom(preview)}
            onRefresh={() => onRefresh(preview)}
            onDelete={() => onDelete(preview)}
            compact
          />
        </div>
      </div>
    </motion.div>
  );
}

function PreviewThumbnail({
  preview,
  size,
  busy,
  onZoom,
  showOverlayActions,
  onRefresh,
  onDelete,
}: {
  preview: TabPreview;
  size: string;
  busy: boolean;
  onZoom: () => void;
  showOverlayActions?: boolean;
  onRefresh?: () => void;
  onDelete?: () => void;
}) {
  const [faviconError, setFaviconError] = useState(false);
  const source = isRenderablePreviewImage(preview.screenshot)
    ? preview.screenshot
    : isRenderablePreviewImage(preview.thumbnail)
      ? preview.thumbnail
      : undefined;
  const fallbackIcon = !faviconError ? (preview.favicon || faviconFromUrl(preview.url)) : '';
  return (
    <div
      className={cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-khiimori-bg-tertiary',
        size,
      )}
    >
      {busy && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-khiimori-bg-secondary/60 backdrop-blur-sm">
          <RefreshCw className="h-5 w-5 animate-spin text-primary-400" />
        </div>
      )}
      {source ? (
        <button
          type="button"
          onClick={onZoom}
          className="h-full w-full"
          aria-label={`Open preview for ${preview.title}`}
        >
          <img
            src={source}
            alt={preview.title}
            className="h-full w-full object-cover object-top"
          />
        </button>
      ) : fallbackIcon ? (
        <button
          type="button"
          onClick={onZoom}
          className="flex h-full w-full flex-col items-center justify-center gap-2 bg-khiimori-bg-secondary/40 p-3"
          aria-label={`Open preview for ${preview.title}`}
        >
          <img
            src={fallbackIcon}
            alt=""
            className="h-10 w-10 rounded-md object-contain"
            onError={() => setFaviconError(true)}
          />
          <span className="line-clamp-2 text-center text-xs text-khiimori-text-muted">{preview.title}</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={onZoom}
          className="flex h-full w-full flex-col items-center justify-center text-khiimori-text-muted"
        >
          <ImageIcon className="h-8 w-8" />
          <span className="mt-1 text-xs">No preview</span>
        </button>
      )}
      {showOverlayActions && (
        <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          {onRefresh && (
            <IconButton
              label="Re-capture preview"
              onClick={onRefresh}
              disabled={busy}
              icon={<RefreshCw className="h-3.5 w-3.5" />}
            />
          )}
          {onDelete && (
            <IconButton
              label="Delete preview"
              onClick={onDelete}
              disabled={busy}
              icon={<Trash2 className="h-3.5 w-3.5" />}
              tone="danger"
            />
          )}
        </div>
      )}
    </div>
  );
}

function RowActions({
  preview,
  busy,
  onZoom,
  onRefresh,
  onDelete,
  compact = false,
}: {
  preview: TabPreview;
  busy: boolean;
  onZoom: () => void;
  onRefresh: () => void;
  onDelete: () => void;
  compact?: boolean;
}) {
  const size = compact ? 'h-7 w-7' : 'h-8 w-8';
  return (
    <div className="flex items-center gap-1">
      <IconButton
        label="Zoom"
        onClick={onZoom}
        icon={<ZoomIn className="h-4 w-4" />}
        className={size}
      />
      <IconButton
        label="Re-capture"
        onClick={onRefresh}
        disabled={busy}
        icon={<RefreshCw className="h-4 w-4" />}
        className={size}
      />
      {preview.url && (
        <a
          href={preview.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open tab in browser"
          className={cn(
            'inline-flex items-center justify-center rounded-md text-khiimori-text-muted hover:bg-khiimori-bg-tertiary hover:text-khiimori-text-primary',
            size,
          )}
        >
          <ExternalLink className="h-4 w-4" />
        </a>
      )}
      <IconButton
        label="Delete preview"
        onClick={onDelete}
        disabled={busy}
        icon={<Trash2 className="h-4 w-4" />}
        tone="danger"
        className={size}
      />
    </div>
  );
}

function IconButton({
  label,
  onClick,
  icon,
  disabled,
  tone = 'default',
  className,
}: {
  label: string;
  onClick: () => void;
  icon: React.ReactNode;
  disabled?: boolean;
  tone?: 'default' | 'danger';
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex items-center justify-center rounded-md transition-colors',
        tone === 'danger'
          ? 'text-khiimori-text-muted hover:bg-error-500/20 hover:text-error-300'
          : 'text-khiimori-text-muted hover:bg-khiimori-bg-tertiary hover:text-khiimori-text-primary',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        className,
      )}
    >
      {icon}
    </button>
  );
}

function PreviewDetail({
  preview,
  busy,
  onRefresh,
  onDelete,
}: {
  preview: TabPreview;
  busy: boolean;
  onRefresh: () => void;
  onDelete: () => void;
}) {
  const source = preview.screenshot || preview.thumbnail;
  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center justify-center overflow-hidden rounded-lg border border-khiimori-border bg-khiimori-bg-tertiary">
        {source ? (
          <img
            src={source}
            alt={preview.title}
            className="max-h-[60vh] w-full object-contain"
          />
        ) : (
          <div className="flex h-48 flex-col items-center justify-center text-khiimori-text-muted">
            <ImageIcon className="h-10 w-10" />
            <span className="mt-2 text-sm">No preview available</span>
          </div>
        )}
      </div>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <DetailRow label="Captured" value={formatCaptured(preview.captured_at)} />
        <DetailRow
          label="Status"
          value={deriveIsStale(preview) ? 'Stale' : 'Fresh'}
        />
        <DetailRow
          label="Size"
          value={formatBytes(preview.size_bytes)}
        />
        <DetailRow
          label="Format"
          value={preview.format ? preview.format.toUpperCase() : '—'}
        />
        {preview.width && preview.height && (
          <DetailRow label="Dimensions" value={`${preview.width} × ${preview.height}`} />
        )}
      </dl>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="secondary"
          leftIcon={<RefreshCw className={cn('h-4 w-4', busy && 'animate-spin')} />}
          onClick={onRefresh}
          disabled={busy}
        >
          Re-capture
        </Button>
        {preview.url && (
          <Button
            variant="secondary"
            leftIcon={<ExternalLink className="h-4 w-4" />}
            onClick={() => window.open(preview.url, '_blank', 'noopener,noreferrer')}
          >
            Open
          </Button>
        )}
        <Button
          variant="danger"
          leftIcon={<Trash2 className="h-4 w-4" />}
          onClick={onDelete}
          disabled={busy}
        >
          Delete
        </Button>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-khiimori-text-muted">{label}</dt>
      <dd className="text-sm text-khiimori-text-primary">{value}</dd>
    </div>
  );
}

export default TabPreviewPage;
